import { useApiQuery } from "@/api";
import { FEATURES } from "@/features/registry";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import useUsers from "@/hooks/useUsers";
import {
  useHasRBACAccessToFeature,
  type FeatureAccessLevel,
} from "@/hooks/useHasRBACAccessToFeature";
import { useHasEngagedWithFeature } from "@/hooks/useHasEngagedWithFeature";
import { isPreviewModeEnabled } from "@/preview-mode/runtime";
import { PREVIEW_MODE_GATED_FEATURES } from "@/preview-mode/supported-features";
import type { Feature } from "@/types/feature";
import { userIsCROAdmin } from "@/util";

export type FeatureAccessState =
  | { state: "loading" }
  | { state: "coming-soon" }
  | { state: "no-permissions" }
  | { state: "needs-ai-consent"; userCanGrantConsent: boolean }
  | { state: "get-started" }
  | { state: "feature"; readOnly: boolean };

export function useFeatureAccess(feature: Feature): FeatureAccessState {
  const config = FEATURES[feature];
  const flag = useFeatureFlags();
  const unlocked = config.unlockFlag == null ? true : flag(config.unlockFlag);

  const { level, loading: roleLoading } = useHasRBACAccessToFeature(feature);
  const { engaged, loading: engagementLoading } =
    useHasEngagedWithFeature(feature);

  const usersState = useUsers();

  const aiChatApproval = useApiQuery(["ai_chat_get_approval_status", {}], {
    enabled: config.requiresAiConsent && unlocked,
    staleTime: 30_000,
  });

  // Preview mode: jump straight to the feature UI for any feature with
  // synthetic-data preview handlers. The splash screens ("Coming soon",
  // "Create your first funnel", etc.) would otherwise hide the very
  // surfaces preview mode is meant to showcase.
  if (isPreviewModeEnabled() && PREVIEW_MODE_GATED_FEATURES.has(feature)) {
    return { state: "feature", readOnly: false };
  }

  // Unlock check is first so locked customers resolve to Coming Soon without
  // waiting on any other query.
  if (!unlocked) {
    return { state: "coming-soon" };
  }

  if (config.requiresRBAC) {
    if (roleLoading) {
      return { state: "loading" };
    }
    if (level === "none") {
      return { state: "no-permissions" };
    }
  }

  if (config.requiresAiConsent) {
    if (aiChatApproval.isLoading) {
      return { state: "loading" };
    }
    const approved = aiChatApproval.data?.approved ?? false;
    if (!approved) {
      return {
        state: "needs-ai-consent",
        userCanGrantConsent: userIsCROAdmin(usersState.currentUser),
      };
    }
  }

  if (config.requiresEngagement) {
    if (engagementLoading) {
      return { state: "loading" };
    }
    if (!engaged) {
      return { state: "get-started" };
    }
  }

  return {
    state: "feature",
    readOnly: config.requiresRBAC && level === "read",
  };
}

export type { FeatureAccessLevel };
